import React, { createContext, useContext, useReducer, useEffect } from 'react';
import ApiCartService from '../services/apiCart'
import { useUser } from '../hooks/useUser'

const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'SET_CART':
      return {
        ...state,
        items: action.payload.carrito || [],
        total: calculateTotal(action.payload.carrito || []),
        itemCount: action.payload.carrito.length || 0,
      };

    case 'ADD_ITEM':
      const existingItem = state.items.find(item => item.productId === action.payload.productId);

      if (existingItem) {
        const updatedItems = state.items.map(item =>
          item.productId === action.payload.productId
            ? { ...item, quantity: item.quantity + action.payload.quantity }
            : item
        );
        return {
          ...state,
          items: updatedItems,
          itemCount: updatedItems.length || 0,
          total: calculateTotal(updatedItems),
        };
      } else {
        const newItems = [...state.items, action.payload];
        return {
          ...state,
          items: newItems,
          itemCount: newItems.length || 0,
          total: calculateTotal(newItems),
        };
      }

    case 'UPDATE_ITEM':
      const updatedItems = state.items.map(item =>
        item.id === action.payload.id
          ? { ...item, quantity: action.payload.quantity }
          : item
      );
      return {
        ...state,
        items: updatedItems,
        itemCount: updatedItems.length || 0,
        total: calculateTotal(updatedItems),
      };

    case 'REMOVE_ITEM':
      const filteredItems = state.items.filter(item => item.id !== action.payload.id);
      return {
        ...state,
        items: filteredItems,
        itemCount: filteredItems.length || 0,
        total: calculateTotal(filteredItems),
      };

    case 'CLEAR_CART':
      return {
        items: [],
        total: 0,
        itemCount: 0,
        loading: false,
        error: null,
      };

    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };

    default:
      return state;
  }
};

const calculateTotal = (items) => {
  return items.reduce((sum, item) => sum + (item.precio * item.cantidad), 0);
};

const initialState = {
  items: [],
  total: 0,
  itemCount: 0,
  loading: false,
  error: null,
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { user } = useUser();

  // Cargar carrito al inicializar
  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = async () => {

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const cartData = await ApiCartService.getCart();
      dispatch({ type: 'SET_CART', payload: cartData });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      // Fallback a localStorage si la API falla
      const localCart = localStorage.getItem('cart');
      if (localCart) {
        dispatch({ type: 'SET_CART', payload: JSON.parse(localCart) });
      }
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const addToCart = async (product, quantity = 1, variedadId = null) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const cartItem = {
        productoId: product.id,
        variedadId: variedadId,
        clienteId: user.id,
        cantidad: quantity,
        precio: product.precio
      }

      try {
        await ApiCartService.addToCart(cartItem);

        await loadCart();
      } catch (apiError) {
        console.warn('API call failed, using local storage:', apiError);
      }

    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const cartItem = {
          cantidad: quantity
        }

        await ApiCartService.updateCartItem(itemId, cartItem);
        await loadCart();
      } catch (apiError) {
        console.warn('API call failed, using local storage:', apiError);
      }

      dispatch({ type: 'UPDATE_ITEM', payload: { id: itemId, quantity } });
      /*
      const updatedItems = state.items.map(item =>
        item.id === itemId ? { ...item, quantity } : item
      );*/
      //localStorage.setItem('cart', JSON.stringify({ ...state, items: updatedItems }));

    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        await ApiCartService.removeFromCart(itemId);

        await loadCart();
      } catch (apiError) {
        console.warn('API call failed, using local storage:', apiError);
      }

      dispatch({ type: 'REMOVE_ITEM', payload: { id: itemId } });

      // Actualizar localStorage
      const updatedItems = state.items.filter(item => item.id !== itemId);
      localStorage.setItem('cart', JSON.stringify({ ...state, items: updatedItems }));

    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const clearCart = async () => {

    try {
        await ApiCartService.deleteCartClient(user.id);
        dispatch({ type: 'CLEAR_CART' });
        await loadCart();
      } catch (apiError) {
        console.warn('API call failed, using local storage:', apiError);
      }

  };

  const value = {
    ...state,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    loadCart,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart debe ser usado dentro de CartProvider');
  }
  return context;
};